import dynamic from 'next/dynamic';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { TimelineMax, TweenMax } from 'gsap';
import { ScrollMagicPluginGsap } from 'scrollmagic-plugin-gsap';
import { useRef } from 'react';
import styles from './index.module.scss';
import { redirectTo404 } from '@/utils';
import { useGlobal } from '@/utils/useGlobal';
import PageLayout from '@/components/PageLayout';
import ScrollMagicFn from '@/utils/ScrollMagic';
import { createEFPic } from '@/utils/useEFPic';
import SEOlinksA from '@/components/SEOlinksA';

const PageConPc = dynamic(() => import('@/components/CES'), {
    ssr: true,
});
const PageConH5 = dynamic(() => import('@/componentsH5/CES'), {
    ssr: true,
});

const { EFPic } = createEFPic('/media/ces');

export default function CES2023({ lang, SEO }: any) {
    const { isMobile } = useGlobal();
    const PageCon = isMobile ? PageConH5 : PageConPc;
    const pageRef = useRef(null);
    const mobileRef = useRef(null);
    const pcRef = useRef(null);
    const topRef = useRef(null);
    const router = useRouter();
    const { locale = 'us' } = router;

    if (typeof window !== 'undefined') {
        window.ScrollMagic = ScrollMagicFn();
        ScrollMagicPluginGsap(ScrollMagic, TweenMax, TimelineMax);
        window.controllerCES = new ScrollMagic.Controller();
    }

    const logoAnimation = (offset: number, offsetTop: number) => {
        const cesDom: any = isMobile ? mobileRef?.current : pcRef?.current;
        const scene = new ScrollMagic.Scene({
            triggerElement: '.ces2023Page',
            offset: offset, //从开始点滚动多少px触发,可为负数
            triggerHook: 0, //触发元素开始进入视口时触发:1 进入,0 离开, 0.5 中间,
            duration: offset,
        });
        const el = isMobile ? '.mobile-logo' : '.pc-logo';
        const tween1 = TweenMax.to(el, 0.3, { opacity: 1 });
        scene.setTween(tween1);
        controllerCES.addScene(scene);

        const scene1 = new ScrollMagic.Scene({
            triggerElement: '.ces2023Page',
            offset: offsetTop, //从开始点滚动多少px触发,可为负数
            triggerHook: 0, //触发元素开始进入视口时触发:1 进入,0 离开, 0.5 中间,
            duration: (pageRef?.current as any).clientHeight - offsetTop,
        });
        scene1.on('progress', () => {
            const scrollTop = document.documentElement.scrollTop;
            const clientHeight = document.documentElement.clientHeight;
            const scrollHeight = document.documentElement.scrollHeight;
            const total = clientHeight * (scrollTop / scrollHeight);
            cesDom.style.top = `${total}px`;
        });
        controllerCES.addScene(scene1);
    };

    useEffect(() => {
        if (isMobile) {
            const topDom: any = topRef?.current;
            const offsetTop = topDom.offsetTop;
            logoAnimation(offsetTop + topDom.clientHeight, offsetTop);
        } else {
            // PC端的数值是固定的，可以直接算
            logoAnimation(276, 230);
        }

        return () => {
            // 先移除scene 再移除controller
            // scene 在子模块内移除
            // 因卸载阶段父组件先触发，所以需setTimeout滞后
            setTimeout(() => {
                window?.controllerCES?.destroy(true);
            }, 30);
        };
    }, []);

    return (
        <div className={`${styles.ces2023Page} ces2023Page`} ref={pageRef}>
            {isMobile && (
                <>
                    <div className="top-logo" ref={topRef}></div>
                    <div className="ces-logo mobile-logo" ref={mobileRef}>
                        <EFPic name={['/h5/sectionA_2.webp', '/h5/sectionA_2.png']} />
                    </div>
                </>
            )}
            {!isMobile && (
                <div className="ces-logo pc-logo" ref={pcRef}>
                    <EFPic name={['/pc/01-4.webp', '/pc/01-4.png']} />
                </div>
            )}
            <PageLayout theme="black">
                <SEO title={lang.seo_title || lang.title} description={lang.seo_desc || ''} />
                <SEOlinksA list={['us', 'ca', 'uk', 'de', 'fr', 'cn']} locale={locale} pathname={router.asPath.substr(1)} />
                <PageCon />
            </PageLayout>
        </div>
    );
}

export async function getServerSideProps({ locale }: { locale: string }) {
    const whiteLocaleList = ['us', 'ca', 'uk', 'de', 'fr', 'eu'];
    if (!whiteLocaleList.includes(locale)) {
        return redirectTo404();
    }
    return {
        props: {},
    };
}
